<template>
  <div>
    <Header :selectedApp="selectedApp"></Header>
    <div class="col-12 d-inline-flex">
      <div class="col-10 no-overflow ms-3 me-3 pt-3 position-relative">
        <h2>Events</h2>
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <a role="button" class="procricket-btn mb-3" @click="redirectToCreate">Create New Entry</a>
            <div class="info-tooltip">
              <img class="info-img" src="@/assets/info.svg" alt="info" /> <span class="info-name">Info</span>
              <div class="info-popover">
                <div class="legend">
                  <div class="legend-row">
                    <span class="legend-swatch event-ok-strong"></span>
                    <span>2 channels active</span>
                  </div>
                  <div class="legend-row">
                    <span class="legend-swatch event-ok-light"></span>
                    <span>1 channel active</span>
                  </div>
                  <div class="legend-row">
                    <span class="legend-swatch status-unknown"></span>
                    <span>no channel active</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center mb-3">
          <button
              class="procricket-btn reorder-btn"
              :class="{ 'is-active': reorderMode }"
              @click="toggleReorderMode"
          >
            <span class="reorder-btn__icon">{{ reorderMode ? '✓' : '≡' }}</span>
            {{ reorderMode ? 'Done ordering' : 'Reorder Events' }}
          </button>

          <span v-if="reorderMode" class="reorder-hint">
            Drag rows using the handle.
          </span>
        </div>
        <!-- TABLE -->
        <div class="table-responsive">
          <table class="table table-striped table-bordered">
            <thead>
            <tr>
              <th v-if="reorderMode" style="width: 34px;"></th>
              <th class="col-1">Event Id</th>
              <th class="col-1">First Matcher</th>
              <th class="col-1">Second Matcher</th>
              <th class="col-1">League</th>
              <th class="col-1">League Id</th>
              <th class="col-1">Order Number</th>
              <th class="col-1">Application</th>
              <th class="col-1">Status</th>
              <th class="col-1">Active</th>
              <th class="col-1">Configure Streams</th>
              <th class="col-1">Action</th>
              <th class="col-1">Edit</th>
            </tr>
            </thead>

            <tbody v-if="!reorderMode">
            <template v-for="config in EventsList" :key="config.eventId">
              <EventTableRow
                  :config="config"
                  :reorderMode="reorderMode"
                  :eventStateClass="getEventStateClass(config.eventId)"
                  :hasRunning="hasRunningChannel(config.eventId)"
                  :canStart="canStartAll(config.eventId)"
                  :startDisabledTitle="startAllDisabledTitle(config.eventId)"
                  @toggle-expand="toggleExpand"
                  @stop-all="stopAllStreams"
                  @start-all="startAllActiveStreams"
              />

              <!-- EXPANDED ROW -->
              <tr>
                <td colspan="12" class="expand-cell">
                  <transition name="expand">
                    <div v-show="expandedEventId === config.eventId" class="expand-wrapper">
                      <div class="centered flex-column mb-2">
                      <span >Channels configured to this event. Drag and Drop on side handle to reorder channels</span> </div>
                      <div class="box-outline">
                        <Draggable
                            :list="channelsForEvent[config.eventId]"
                            item-key="channelId"
                            handle=".channel-drag-handle"
                            :animation="150"
                            :options="{ filter: 'button,a,input,select,textarea', preventOnFilter: false }"
                            @end="onChannelsDragEnd(config.eventId)"
                        >
                          <template #item="{ element: channel }">
                            <EventChannelCard
                                :channel="channel"
                                :role="getChannelRole(channel, config.eventId)"
                                :event="config"
                                :startEligibility="canStartChannel"
                                @start-channel="startChannel"
                                @stop-channel="stopChannel"
                                @reload-stream="reloadChannelMetadata"
                                @reload-event="reloadEventMetadata"
                                @toggle-active="toggleChannelActive"
                            />
                          </template>
                        </Draggable>

                        <div v-if="(channelsForEvent[config.eventId] || []).length === 0" class="text-muted text-center p-3">
                          No channels assigned to this event
                        </div>
                      </div>
                    </div>
                  </transition>
                </td>
              </tr>
            </template>
            </tbody>

            <Draggable
                v-else
                tag="tbody"
                v-model="EventsList"
                item-key="eventId"
                :animation="150"
                ghost-class="drag-ghost"
                chosen-class="drag-chosen"
                drag-class="drag-drag"
                :delay="150"
                :delayOnTouchOnly="true"
                @start="isDragging = true; expandedEventId = null"
                @end="isDragging = false; onEventsDragEnd()"
            >
              <template #item="{ element: config }">
                <EventTableRow
                    :config="config"
                    :reorderMode="reorderMode"
                    :eventStateClass="getEventStateClass(config.eventId)"
                    :hasRunning="hasRunningChannel(config.eventId)"
                    :canStart="canStartAll(config.eventId)"
                    :startDisabledTitle="startAllDisabledTitle(config.eventId)"
                    @toggle-expand="toggleExpand"
                    @stop-all="stopAllStreams"
                    @start-all="startAllActiveStreams"
                />
              </template>
            </Draggable>
          </table>
        </div>
      </div>
    </div>
    <!-- PAGE HELP / EXPLANATION -->
    <div class="mt-3">
      <div class="channel-details-card p-3">
        <div class="d-flex align-items-center justify-content-between">
          <h5 class="mb-0">How this page works</h5>

          <!-- Optional: collapse/expand -->
          <button
              class="btn btn-sm btn-outline-secondary"
              type="button"
              @click="showHelp = !showHelp"
          >
            {{ showHelp ? 'Hide' : 'Show' }}
          </button>
        </div>

        <div v-show="showHelp" class="mt-3 small">
          <div class="mb-3">
           <div class="mb-3">
            <div class="fw-semibold mb-1">Event health indicator</div>
            <ul class="mb-0">
              <li><span class="legend-swatch event-ok-strong"></span> <strong>2+ active channels</strong> are running.</li>
              <li><span class="legend-swatch event-ok-light"></span> <strong>1 active channel</strong> is running.</li>
              <li><span class="legend-swatch status-unknown"></span> <strong>No active channels</strong> are running.</li>
            </ul>
          </div>

          <div class="mb-3">
            <div class="fw-semibold mb-1">Actions</div>
            <ul class="mb-0">
              <li><strong>Configure Channels and Streams</strong> opens edit of channels and configuring streams per event.</li>
              <li><strong>Edit Event</strong> opens the edit form for the selected event.</li>
              <li><strong>Start</strong> starts all eligible active channels for the event; <strong>Stop</strong> stops all running active channels.</li>
            </ul>
          </div>
            <div class="mb-3">
              <div class="fw-semibold mb-1">Ordering the events:</div>
              <ul class="mb-0">
                <li>By clicking the Reorder Events button a draggable options displays at the start of each row in the table. By dragging you reorder them with changing their <code>orderNumber</code>. When finished click Done Ordering to save the changes.</li>
                </ul>
            </div>

          <div>
            <div class="fw-semibold mb-1">Channels (expanded view)</div>
            <ul class="mb-0">
              <li>Here is the list of all the channels which are connected to the event</li>
              <li>Each channel card shows: channel details, encoder data, and configuration issues (why a channel cannot start). Starting from here starts only that channel.</li>
              <li>Only <strong>active</strong> channels can be started.</li>
              <li>Channel cards are <strong>draggable</strong> and with every drag, changes are made to the BE database.</li>
              <li>Role labeling (<strong>MAIN / BACKUP</strong>) is derived from the <strong>orderNumber</strong> among active channels.</li>
              <li>Available tools include: </li>
              <li> <img src="@/assets/chart-line.svg" alt="" class="btn-icon" /> open Kibana graphs, which opens up the kibana Channel analytics page for this channel</li>
              <li><img src="@/assets/reload.svg" alt="" class="btn-icon" /> <strong>Channel</strong> Reload, which fetches the data from the encoder site based on stream id with the lst time it has been updated</li>
              <li><img src="@/assets/reload.svg" alt="" class="btn-icon" /> <strong>Event</strong> Reload, which fetches the data for every channel for that event from the encoder site based on stream id with the lst time it has been updated</li>
              <li><img src="@/assets/settings.svg" alt="" class="btn-icon" /> manage channel settings in EventBroadcast with this channel selected.</li>
            </ul>
          </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import AdminService from "@/services/admin-service";
import Header from "@/components/Header.vue";
import Cookies from "js-cookie";
import EventChannelCard from "@/components/EventChannelCard.vue";
import XaccelService from "@/services/xaccel-service";
import {formatUnixTimestamp, truncateText} from "@/utils/general-functions";
import {getStartEligibility, getChannelMetadata, parseMetadata} from "@/utils/bapi-functions";
import Draggable from "vuedraggable";
import EventTableRow from "@/components/EventTableRow.vue";

export default {
  name: "Events",
  components: {
    Header,
    EventChannelCard,
    Draggable,
    EventTableRow,
  },
  data() {
    return {
      EventsList: [],
      selectedApp: "",
      ChannelsList: [],
      expandedEventId: null,
      showHelp: true,
      channelsForEvent: {},
      // NEW: reorder mode state
      reorderMode: false,
      isDragging: false,
      reorderBeforeMap: null,
    };
  },

  created() {
    const appCookie = Cookies.get("application");
    if (appCookie) this.selectedApp = appCookie;

    this.doLoadEvents();
    this.doLoadChannels();
  },
  methods: {
    truncateText,

    async toggleReorderMode() {
      if (!this.reorderMode) {
        this.reorderBeforeMap = Object.fromEntries(
            this.EventsList.map((e, idx) => [String(e.eventId), idx])
        );

        this.reorderMode = true;
        this.expandedEventId = null;
        return;
      }
      this.reorderMode = false;
      this.expandedEventId = null;
      const before = this.reorderBeforeMap || {};
      this.EventsList = this.EventsList.map((e, idx) => ({ ...e, orderNumber: idx + 1 }));

      // Build updates only for items whose position changed
      const updates = [];
      for (let i = 0; i < this.EventsList.length; i++) {
        const e = this.EventsList[i];
        const id = String(e.eventId);

        const oldIdx = before[id];
        const newIdx = i;

        if (oldIdx === undefined || oldIdx !== newIdx) {
          updates.push(e);
        }
      }
      this.reorderBeforeMap = null;
      if (!updates.length) return;
      try {
        for (const ev of updates) {
          await AdminService.updateEvent(ev);
        }
        this.doLoadEvents();
      } catch (err) {
        console.error("Failed updating reordered events:", err);
        alert(err?.message || "Failed to save new order.");
        this.doLoadEvents();
      }
    },
    toggleExpand(eventId) {
      const willOpen = this.expandedEventId !== eventId;
      this.expandedEventId = willOpen ? eventId : null;
      if (willOpen) {
        this.buildChannelsForEvent(eventId);
      }
    },

    getChannelsForEvent(eventId) {
      return this.ChannelsList
          .filter(c => (c.eventIds || []).map(Number).includes(Number(eventId)))
          .sort((a, b) => Number(a.orderNumber) - Number(b.orderNumber));
    },
    buildChannelsForEvent(eventId) {
      this.channelsForEvent[eventId] = this.getChannelsForEvent(eventId).slice();
    },
    doLoadEvents() {
      AdminService.getEventsList()
          .then((data) => {
            // Always sort by orderNumber on load (like before)
            if (this.selectedApp !== '') {
              data = data.filter(item => item.application === this.selectedApp);
            }
            this.EventsList = (data || []).slice().sort((a, b) => {
              const aOrd = a.orderNumber == null ? Number.MAX_SAFE_INTEGER : Number(a.orderNumber);
              const bOrd = b.orderNumber == null ? Number.MAX_SAFE_INTEGER : Number(b.orderNumber);
              return aOrd - bOrd;
            });
            sessionStorage.setItem("events", JSON.stringify(this.EventsList));
          })
          .catch((error) => {
            console.error("Error loading events:", error);
          });
    },
    doLoadChannels() {
      AdminService.getChannelsList(this.selectedApp).then((data) => {
        this.ChannelsList = data;
        if (this.expandedEventId != null) {
          this.buildChannelsForEvent(this.expandedEventId);
        }
      });
    },
    async onChannelsDragEnd(eventId) {
      const list = this.channelsForEvent[eventId] || [];
      const updates = [];
      list.forEach((ch, idx) => {
        const newOrder = idx + 1;
        if (Number(ch.orderNumber) !== newOrder) {
          updates.push({ ...ch, orderNumber: newOrder });
        }
      });
      if (!updates.length) return;
      try {
        // persist only changed ones
        for (const ch of updates) {
          await AdminService.updateChannel({ ...ch, orderNumber: ch.orderNumber, active: null, status: null });
        }
        this.doLoadChannels();
        this.buildChannelsForEvent(eventId);
      } catch (err) {
        console.error("Failed updating reordered channels:", err);
        alert(err?.message || "Failed to save channel ordering.");
        this.doLoadChannels();
        this.buildChannelsForEvent(eventId);
      }
    },

    redirectToCreate() {
      this.$router.push({ name: "EventCreate" });
    },

    onEventsDragEnd() {
      this.EventsList = this.EventsList.map((e, idx) => ({
        ...e,
        orderNumber: idx + 1,
      }));
    },
    async startChannel(row) {
      let parsed = getChannelMetadata(row)
      try {
        if (!parsed.encoder || !parsed.id) {
          throw new Error("Missing encoder or streamId");
        }

        await XaccelService.startXaccelStream(parsed.encoder, parsed.id);
        this.doLoadChannels();
      } catch (err) {
        console.error("Failed to start stream", err);
        alert("Failed to start stream.");
      }
    },

    async stopChannel(row) {
      let parsed = getChannelMetadata(row)
      try {
        if (!parsed.encoder || !parsed.id) {
          throw new Error("Missing encoder or streamId");
        }

        await XaccelService.stopXaccelStream(parsed.encoder, parsed.id);
        this.doLoadChannels();
      } catch (err) {
        console.error("Failed to start stream", err);
        alert("Failed to start stream.");
      }
    },
    hasRunningChannel(eventId) {
      return this.ChannelsList.some(
          (c) => (c.eventIds || []).map(Number).includes(Number(eventId)) && (c.status === true || c.status === "true")
      );
    },

    canStartAll(eventId) {
      const channels = this.getChannelsForEvent(eventId).filter(
          (c) => c.active === true
      );

      return channels.some((ch) => this.canStartChannel(ch).canStart);
    },

    startAllDisabledTitle(eventId) {
      const channels = this.getChannelsForEvent(eventId).filter(
          (c) => c.active === true
      );
      if (!channels.length) return "No active channels";
      const firstFail = channels.map((ch) => this.canStartChannel(ch)).find((r) => !r.canStart);
      return firstFail ? firstFail.reason : "Start stream";
    },

    async startAllActiveStreams(eventId) {
      const channels = this.getChannelsForEvent(eventId).filter(
          (c) => c.active === true
      );

      for (const ch of channels) {
        const elig = this.canStartChannel(ch);
        if (elig.canStart) await this.startChannel(ch);
      }
      this.doLoadChannels();
    },

    async stopAllStreams(eventId) {
      const channels = this.getChannelsForEvent(eventId)
          .filter((c) => c.active === true)
          .filter((c) => c.status === true);

      for (const ch of channels) {
        await this.stopChannel(ch);
      }
      this.doLoadChannels();
    },
    getEventStateClass(eventId) {
      const activeChannelsCount = this.ChannelsList.filter(
          (c) =>
              Number(c.eventId) === Number(eventId) &&
              (c.active === true) &&
              (c.status === true)
      ).length;

      if (activeChannelsCount >= 2) return "event-ok-strong";
      if (activeChannelsCount === 1) return "event-ok-light";
      return "";
    },

    async reloadChannelMetadata({ channelId, encoder, stream }) {
      try {
        const channel = this.ChannelsList.find((c) => Number(c.channelId) === Number(channelId));
        const response = await XaccelService.getXaccelStreamMetadata(encoder, stream.id);
        const timestamp = formatUnixTimestamp(Date.now());

        const updatedChannel = {
          ...channel,
          active: null,
          status: null,
          providersMetadata: JSON.stringify({ ...response, timestamp }),
        };

        await AdminService.updateChannel(updatedChannel);
        this.doLoadChannels();
      } catch (err) {
        console.error("Failed to reload channel data from Encoder", err);
        alert("Could not reload stream data from Encoder.");
      }
    },
    async reloadEventMetadata() {
      try {
        const eventId = Number(this.expandedEventId);
        if (!eventId) return;

        // all channels for the expanded event
        const channels = (this.ChannelsList || []).filter(
            (c) => (c.eventIds || []).map(Number).includes(eventId)
        );

        if (!channels.length) {
          console.warn("No channels found for event", eventId);
          return;
        }
        for (const channel of channels) {
          if (!channel.providersMetadata || channel.providersMetadata.trim() === "{}") continue;
          const meta = parseMetadata(channel.providersMetadata);
          const response = await XaccelService.getXaccelStreamMetadata(meta.encoder, meta.id);
          const timestamp = formatUnixTimestamp(Date.now());

          // 3) build updated channel payload
          const updatedChannel = {
            ...channel,
            active: null,
            status: null,
            providersMetadata: JSON.stringify({ ...response, timestamp }),
          };
          await AdminService.updateChannel(updatedChannel);
        }
        await this.doLoadChannels();
      } catch (err) {
        console.error("Failed to reload event metadata from Encoder", err);
        alert("Could not reload stream data from Encoder.");
      }
    },

    getChannelRole(channel, eventId) {
      const eventChannels = this.ChannelsList.filter((c) => {
        return (c.eventIds || []).map(Number).includes(Number(eventId));
      });
      if (!eventChannels.length) return null;

      const activeChannels = eventChannels.filter((c) => c.active === true);
      if (!activeChannels.length) return null;

      const sortedActive = [...activeChannels].sort(
          (a, b) =>
              (a.orderNumber ?? Number.MAX_SAFE_INTEGER) -
              (b.orderNumber ?? Number.MAX_SAFE_INTEGER)
      );

      const idx = sortedActive.findIndex((c) => c.channelId === channel.channelId);
      if (idx === -1) return null;

      if (idx === 0) return "MAIN";
      if (idx === 1) return "BACKUP";
      return `BACKUP ${idx}`;
    },

    canStartChannel(channel) {
      return getStartEligibility(channel);
    },

    async toggleChannelActive({ channelId, active }) {
      try {
        const channel = this.ChannelsList.find((c) => Number(c.channelId) === Number(channelId));
        if (!channel) return;

        const updatedChannel = { ...channel, status: null, active };
        await AdminService.updateChannel(updatedChannel);
        this.doLoadChannels();
      } catch (err) {
        console.error("Failed to update channel active:", err);
        alert("Could not update channel status.");
      }
    },
  },
};
</script>

<style src="../../assets/style.css">
</style>
